import { useMemo, useRef, useState } from 'react'
import type { MouseEvent, MutableRefObject, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ComposedChart, Scatter, Line, XAxis, YAxis, CartesianGrid, Customized,
  Legend, ResponsiveContainer,
} from 'recharts'
import type { StaatidRoundtripRow, StaatidMerkeRow } from './api'
import { dateLocale } from '../../../i18n/format'

// Same palette family as WeeklyTrendChart — keep chart colors consistent.
const SERIES_COLORS = ['#06b6d4', '#f97316', '#8b5cf6', '#10b981', '#ef4444', '#3b82f6', '#ec4899', '#eab308']
// Above this many merker the per-merke split stops being readable — fall back
// to a single series + one combined trend line.
const MAX_MERKE_SERIES = 8
// Dealer x merke pairs (#471) are capped at 3 dealers x 3 merker by the
// selection guard in SporringTab — one dash style per merke, reused across
// dealers (color already disambiguates the dealer).
const MAX_PAIR_SERIES = 9
const DASH_PATTERNS = ['', '6 3', '2 2']

interface Props {
  rows: StaatidRoundtripRow[]
  total: number
  /** Per-merke stats incl. monthly trend — from /staatid/merker with the SAME
   *  filters as the scatter, so the median lines are exact (not sample-based). */
  merker: StaatidMerkeRow[]
  /** Combined monthly trend (from /oversikt) — used when > MAX_MERKE_SERIES.
   *  `antall` is the month's sale count; the data table under the chart (#531)
   *  reports it. Optional so a caller that only has medians still type-checks. */
  combinedTrend: {
    maned: string
    antall?: number
    median_dager: number | null
    snitt_dager?: number | null
  }[]
  /** Exact totals for the combined series, so the legend can carry
   *  antall/median/snitt in the un-split case too (#490 AC3/AC6). */
  combinedStats?: { antall: number; median_dager: number | null; snitt_dager: number | null }
  loading: boolean
  /** Active month zoom (YYYY-MM) and its setter; null = full period. */
  zoomMonth: string | null
  onZoomMonth: (month: string | null) => void
  /** Series dimension: merke (default), modell (drilldown), dealer, or
   *  dealer-merke pairs (#471, 2+ dealers x 2+ merker at once) — the
   *  caller normalizes /forhandlere/trend rows into the same merker shape,
   *  using the dealer name as `merke` and (for pairs) the pair's own merke
   *  as `pairMerke` (#274 dealer-comparison follow-up). */
  groupBy?: 'merke' | 'modell' | 'dealer' | 'dealer-merke'
  /** Opens the roundtrip trail drilldown for the clicked point (#274 Fase 3). */
  onPointClick?: (sluttSvvId: number) => void
  /** Extra context appended to each series label when comparing dealers
   *  under a single active merke filter (e.g. "BILIA" -> "BILIA — Toyota")
   *  — makes the already-applied merke scoping visible in the legend
   *  instead of silently narrowing the data with no visual cue. Only
   *  applied when groupBy === 'dealer'. */
  seriesLabelSuffix?: string
  /** Render the numbers behind the plot as a table underneath (#531). Default
   *  true. MerkeDetalj turns it off: its MonthlyTrendChart (#530) already shows
   *  exactly this series month by month, so the page would carry the same table
   *  twice. ForhandlerDetalj turns it off too (#655). The same numbers are in
   *  the hover tooltip either way. */
  showTable?: boolean
}

interface Point {
  sluttSvvId: number
  ts: number
  dager: number
  merke: string | null
  modell: string | null
  forhandler: string | null
}

/** "45 d" / "—" — legend/label formatting for a nullable day count. */
function fmtDager(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : `${v.toLocaleString('nb-NO')} d`
}

/** One entry in the "what the lines mean" strip (#531 AC3/AC4). Line styles are
 *  drawn rather than described, next to the word for them. */
function LegendKey(
  { label, hint, stroke }: { label: string; hint: string; stroke?: 'solid' | 'dashed' | 'dotted' },
) {
  const border = stroke === 'dashed' ? 'border-dashed'
    : stroke === 'dotted' ? 'border-dotted'
    : 'border-solid'
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-block w-5 border-t-2 ${border} ${
          stroke ? 'border-gray-500 dark:border-gray-400' : 'border-transparent'
        }`}
        aria-hidden
      />
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <span className="text-gray-500 dark:text-gray-400">— {hint}</span>
    </span>
  )
}

function monthTs(maned: string): number {
  // Anchor trend points mid-month so the line sits naturally among the dots.
  return new Date(`${maned}-15T00:00:00`).getTime()
}

/** Centered rolling average (default 3-month window) — smooths month-to-month
 *  noise so the trend reads as a trend, not a point-to-point zigzag. Window
 *  shrinks at the edges rather than dropping the first/last points. */
export function smoothCentered(pts: { ts: number; median: number }[], window = 3): { ts: number; median: number }[] {
  const half = Math.floor(window / 2)
  return pts.map((p, i) => {
    const slice = pts.slice(Math.max(0, i - half), Math.min(pts.length, i + half + 1))
    const avg = slice.reduce((sum, s) => sum + s.median, 0) / slice.length
    return { ts: p.ts, median: avg }
  })
}

/** Catmull-Rom interpolation of a single scalar through 4 control points at
 *  parameter t in [0, 1] between p1 and p2. */
function catmullRomAt(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t
  const t3 = t2 * t
  return 0.5 * (
    2 * p1 +
    (p2 - p0) * t +
    (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
    (3 * p1 - p0 - 3 * p2 + p3) * t3
  )
}

/** Turns a handful of monthly points into a genuinely smooth curve. Recharts'
 *  own curve types (e.g. type="monotone") interpolate BETWEEN the points it's
 *  given — with only ~9 monthly vertices over a typical period, that still
 *  reads as "a line between the data points", not a curve (reported: "it is
 *  just a line between the data points"). This resamples a Catmull-Rom spline
 *  through the points at fine (daily-ish) intervals first, so there's enough
 *  vertex density for any curve type to actually look smooth.
 *
 *  Only `median` (the Y value) is splined — `ts` (X, time) is interpolated
 *  LINEARLY within each segment, which guarantees the output stays
 *  chronologically monotonic regardless of how unevenly the input points are
 *  spaced (Catmull-Rom on X doesn't have that guarantee). Fewer than 3 points
 *  can't form a curve (no interior segment has neighbors on both sides) —
 *  returned unchanged as a straight line, same as today. */
export function resampleCatmullRom(
  pts: { ts: number; median: number }[], stepsPerSegment = 12,
): { ts: number; median: number }[] {
  if (pts.length < 3) return pts
  const out: { ts: number; median: number }[] = []
  const last = pts.length - 2
  for (let i = 0; i <= last; i++) {
    const p0 = pts[Math.max(0, i - 1)]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[Math.min(pts.length - 1, i + 2)]
    // Include the segment's final point only on the last segment, so shared
    // endpoints between segments aren't duplicated.
    const steps = i === last ? stepsPerSegment + 1 : stepsPerSegment
    for (let s = 0; s < steps; s++) {
      const t = s / stepsPerSegment
      out.push({
        ts: p1.ts + (p2.ts - p1.ts) * t,
        median: catmullRomAt(p0.median, p1.median, p2.median, p3.median, t),
      })
    }
  }
  return out
}

/** "YYYY-MM" -> the next calendar month, also "YYYY-MM". */
function nextMonth(maned: string): string {
  const [y, m] = maned.split('-').map(Number)
  const d = new Date(y, m, 1) // m is already 1-indexed -> rolls to next month
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** Splits a monthly trend into contiguous runs of adjacent months. A pair's
 *  trend (#471) can have thin months omitted entirely (backend gaps cells
 *  with < 3 sales rather than showing a noisy single-sale median) — feeding
 *  the full array straight into smoothCentered/resampleCatmullRom would
 *  interpolate a smooth curve straight THROUGH that gap, invisibly. Each
 *  run is smoothed/resampled independently by the caller and rendered as
 *  its own <Line>, so a gap becomes an actual visible break. */
export function splitIntoContiguousRuns(
  trend: { maned: string; median_dager: number | null }[],
): { ts: number; median: number }[][] {
  const pts = trend.filter(t => t.median_dager !== null)
  const runs: { ts: number; median: number }[][] = []
  let prevManed: string | null = null
  for (const t of pts) {
    const point = { ts: monthTs(t.maned), median: t.median_dager as number }
    if (prevManed && nextMonth(prevManed) === t.maned) {
      runs[runs.length - 1].push(point)
    } else {
      runs.push([point])
    }
    prevManed = t.maned
  }
  return runs
}

/** "YYYY-MM" of a timestamp in local time — the calendar monthTs() and the
 *  backend's `maned` use. */
function monthOf(ts: number): string {
  const d = new Date(ts)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

interface MonthFigures {
  maned: string
  antall?: number
  median_dager: number | null
  snitt_dager?: number | null
}

interface TooltipSeries {
  key: string
  label: string
  color: string
  trend: MonthFigures[]
}

/** The hovered month's numbers per series (#655) — what the table under the
 *  chart lists, one month at a time — plus the sale under the pointer, if any. */
function TooltipContent({ maned, series, hovered }: {
  maned: string
  series: TooltipSeries[]
  hovered: Point | null
}) {
  const { t: tr } = useTranslation('staatid')
  const rows = series.flatMap(s => {
    const t = s.trend.find(x => x.maned === maned)
    return t ? [{ ...s, t }] : []
  })
  return (
    <div
      data-testid="staatid-scatter-tooltip"
      className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-sm shadow-xs px-3 py-2 text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap"
    >
      {hovered && (
        <div
          data-testid="staatid-scatter-tooltip-sale"
          className="mb-1.5 pb-1.5 border-b border-gray-200 dark:border-dark-border"
        >
          <p className="font-medium">{[hovered.merke, hovered.modell].filter(Boolean).join(' ') || '—'}</p>
          {hovered.forhandler && <p className="text-gray-500 dark:text-gray-400">{hovered.forhandler}</p>}
          <p>
            {tr('common.dateDager', { date: new Date(hovered.ts).toLocaleDateString('nb-NO'), n: hovered.dager.toLocaleString('nb-NO') })}
          </p>
        </div>
      )}
      <p className="font-medium text-gray-900 dark:text-gray-100">{tr('common.maanedValue', { maned })}</p>
      {rows.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400">{tr('scatter.ingenSluttsalg')}</p>
      ) : (
        <table className="mt-1">
          <thead>
            <tr className="text-gray-500 dark:text-gray-400">
              {series.length > 1 && <th className="pr-3 text-left font-normal">{tr('common.serie')}</th>}
              <th className="pr-3 text-right font-normal">{tr('common.antallSluttsalg')}</th>
              <th className="pr-3 text-right font-normal">{tr('common.median')}</th>
              <th className="text-right font-normal">{tr('common.snitt')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} data-testid="staatid-scatter-tooltip-row">
                {series.length > 1 && (
                  <td className="pr-3">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                      style={{ backgroundColor: r.color }}
                      aria-hidden
                    />
                    {r.label}
                  </td>
                )}
                <td className="pr-3 text-right tabular-nums">
                  {r.t.antall === undefined ? '—' : r.t.antall.toLocaleString('nb-NO')}
                </td>
                <td className="pr-3 text-right tabular-nums">{fmtDager(r.t.median_dager)}</td>
                <td className="text-right tabular-nums">{fmtDager(r.t.snitt_dager)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Where the plot area sits and how x maps to time — read off the rendered
 *  chart by <Customized>, so it is always the chart's own scale. */
interface PlotGeometry {
  left: number
  top: number
  width: number
  height: number
  invert: (x: number) => number
}

/**
 * Hover tooltip for the scatter (#655). Recharts' own <Tooltip> cannot do this
 * on this chart: every series brings its own data array on a numeric x axis,
 * so the shared tooltip only fired over a dot, and then looked each series up
 * by that dot's index — the payload belonged to an unrelated point. This layer
 * turns the pointer's x into a date with the chart's scale and reads the month
 * off that instead.
 *
 * It owns all hover state — the pointer and the dot under it — so hovering
 * re-renders the tooltip only: `children` (the chart, often thousands of dots)
 * is the same element between moves, and React skips it.
 */
function HoverLayer({ geometry, dotHover, series, children }: {
  geometry: MutableRefObject<PlotGeometry | null>
  /** Set here, called by the Scatter's enter/leave handlers. */
  dotHover: MutableRefObject<(p: Point | null) => void>
  series: TooltipSeries[]
  children: ReactNode
}) {
  const [pos, setPos] = useState<{ x: number; y: number; maned: string; flip: boolean } | null>(null)
  const [hovered, setHovered] = useState<Point | null>(null)
  dotHover.current = setHovered

  function onMove(e: MouseEvent<HTMLDivElement>) {
    const g = geometry.current
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    if (!g || x < g.left || x > g.left + g.width || y < g.top || y > g.top + g.height) {
      setPos(null)
      return
    }
    setPos({ x, y, maned: monthOf(g.invert(x)), flip: x > rect.width / 2 })
  }

  // A dot knows its own date exactly; the pointer is a pixel or two off it.
  const maned = hovered ? monthOf(hovered.ts) : pos?.maned

  return (
    <div
      className="relative"
      data-testid="staatid-scatter-hover"
      onMouseMove={onMove}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && maned && (
        <div
          className="absolute z-10 pointer-events-none"
          style={{
            left: pos.x,
            top: pos.y,
            transform: `translate(${pos.flip ? 'calc(-100% - 12px)' : '12px'}, 12px)`,
          }}
        >
          <TooltipContent maned={maned} series={series} hovered={hovered} />
        </div>
      )}
    </div>
  )
}

/**
 * Scatter of individual roundtrips (slutt_dato × ståtid) colored per merke,
 * with monthly-median trend lines per merke. Month chips below the chart zoom
 * the period to one month (parent reloads without sampling).
 */
export default function MerkeScatter({
  rows, total, merker, combinedTrend, combinedStats, loading, zoomMonth, onZoomMonth,
  groupBy = 'merke', onPointClick, seriesLabelSuffix, showTable = true,
}: Props) {
  const { t } = useTranslation('staatid')
  // Dots/lines visibility — independent of the data itself, so it's plain
  // local UI state rather than a prop the caller needs to thread through.
  const [showDots, setShowDots] = useState(true)
  const [showLines, setShowLines] = useState(true)
  // Median and mean answer different questions — both are drawn (#490 AC2/AC5),
  // the mean dashed in the same colour, and either can be switched off.
  const [showSnitt, setShowSnitt] = useState(true)
  // #531 — the smoothed Trend curve does not visibly pass through the monthly
  // medians, so "where is March actually?" had no answer on the chart. This
  // draws the raw monthly medians joined point-to-point, with a dot per month,
  // ALONGSIDE the smoothed curve rather than replacing it (#490 added the
  // smoothing deliberately). Off-switchable: with 9 pair series it doubles the
  // line count.
  const [showPunkter, setShowPunkter] = useState(true)
  // Wired to HoverLayer, which owns the hover state (see there).
  const geometry = useRef<PlotGeometry | null>(null)
  const dotHover = useRef<(p: Point | null) => void>(() => {})

  const split = merker.length > 0 && merker.length <= (groupBy === 'dealer-merke' ? MAX_PAIR_SERIES : MAX_MERKE_SERIES)

  // Pair-mode series key — must match on BOTH sides: the raw scatter rows
  // (each roundtrip already carries its own dealer AND its own car's
  // merke, no backend change needed there) and the merker/trendLines rows
  // (SporringTab maps dealer_org_navn -> merke, the pair's own merke ->
  // pairMerke, per #471).
  const pairKey = (dealer: string | null, merke: string | null) => `${dealer ?? '—'}||${merke ?? '—'}`

  const byMerke = useMemo(() => {
    const groups: Record<string, Point[]> = {}
    for (const r of rows) {
      // StaatidRoundtripRow has no generic `dealer` field — dealer-compare
      // mode groups by the dealer name on each row instead.
      const key = !split ? 'alle'
        : groupBy === 'dealer-merke' ? pairKey(r.dealer_org_navn, r.merke)
        : groupBy === 'dealer' ? (r.dealer_org_navn ?? '—')
        : (r[groupBy] ?? '—')
      ;(groups[key] ??= []).push({
        sluttSvvId: r.slutt_svv_id,
        ts: new Date(r.slutt_dato).getTime(),
        dager: r.staatid_dager,
        merke: r.merke,
        modell: r.modell,
        forhandler: r.dealer_org_navn,
      })
    }
    return groups
  }, [rows, split, groupBy])

  // Pair-mode style (#471) — color by dealer (so "BILIA — Toyota" and
  // "BILIA — BMW" share a color), dash by merke. Single source of truth,
  // keyed by the same composite pairKey used for scatter grouping AND
  // trend lines below, plus Scatter's fill — so dots/legend/lines all
  // agree on which color+label belong to which pair.
  const pairStyle = useMemo(() => {
    const map = new Map<string, { color: string; dash: string; label: string }>()
    if (groupBy !== 'dealer-merke') return map
    const dealerOrder = [...new Set(merker.map(m => m.merke ?? '—'))]
    const merkeOrder = [...new Set(merker.map(m => m.pairMerke ?? '—'))]
    for (const m of merker) {
      const dealer = m.merke ?? '—'
      const merke = m.pairMerke ?? '—'
      map.set(pairKey(dealer, merke), {
        color: SERIES_COLORS[dealerOrder.indexOf(dealer) % SERIES_COLORS.length],
        dash: DASH_PATTERNS[merkeOrder.indexOf(merke) % DASH_PATTERNS.length],
        label: `${dealer} — ${merke}`,
      })
    }
    return map
     
  }, [groupBy, merker])

  // One line per merke from the exact per-merke monthly medians; a merke's
  // line needs >= 2 months to be drawable. Pair mode (#471) is a separate
  // branch: each pair's trend is split into contiguous month-runs first so
  // a thin (dealer, merke, month) cell the backend omitted renders as an
  // actual gap, not a curve smoothed straight through it.
  const trendLines = useMemo(() => {
    if (!split) {
      const pts = combinedTrend
        .filter(t => t.median_dager !== null)
        .map(t => ({ ts: monthTs(t.maned), median: t.median_dager as number }))
      const snittPts = combinedTrend
        .filter(t => t.snitt_dager !== null && t.snitt_dager !== undefined)
        .map(t => ({ ts: monthTs(t.maned), median: t.snitt_dager as number }))
      return pts.length >= 2
        ? [{
            name: 'Alle',
            color: SERIES_COLORS[0],
            dash: '',
            legend: true,
            pts: resampleCatmullRom(smoothCentered(pts)),
            snittPts: snittPts.length >= 2 ? resampleCatmullRom(smoothCentered(snittPts)) : [],
            // Unsmoothed, one vertex per month (#531).
            rawPts: pts,
          }]
        : []
    }
    if (groupBy === 'dealer-merke') {
      return merker.flatMap(m => {
        const key = pairKey(m.merke ?? '—', m.pairMerke ?? '—')
        const style = pairStyle.get(key)
        const runs = splitIntoContiguousRuns(m.trend).filter(run => run.length >= 2)
        return runs.map((run, i) => ({
          name: key,
          color: style?.color ?? SERIES_COLORS[0],
          dash: style?.dash ?? '',
          legend: i === 0, // only the first run of a pair gets a legend entry
          pts: resampleCatmullRom(smoothCentered(run)),
          // Pair mode already carries up to 9 gapped lines; a mean line each
          // would double that into noise, so it stays median-only.
          snittPts: [] as { ts: number; median: number }[],
          rawPts: run,
        }))
      })
    }
    return merker
      .map((m, i) => ({
        name: (groupBy === 'modell' ? m.modell : m.merke) ?? '—',
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        dash: '',
        legend: true,
        pts: m.trend
          .filter(t => t.median_dager !== null)
          .map(t => ({ ts: monthTs(t.maned), median: t.median_dager as number })),
        snittPts: m.trend
          .filter(t => t.snitt_dager !== null && t.snitt_dager !== undefined)
          .map(t => ({ ts: monthTs(t.maned), median: t.snitt_dager as number })),
      }))
      .filter(l => l.pts.length >= 2)
      .map(l => ({
        ...l,
        rawPts: l.pts,
        pts: resampleCatmullRom(smoothCentered(l.pts)),
        snittPts: l.snittPts.length >= 2 ? resampleCatmullRom(smoothCentered(l.snittPts)) : [],
      }))
     
  }, [split, merker, combinedTrend, groupBy, pairStyle])

  // The numbers behind the plot, per series and month (#531 AC1/AC2). Read
  // from the SAME arrays the lines are built from — `merker[].trend` when
  // split, `combinedTrend` otherwise — so the table cannot drift from the
  // chart. Months with no median are kept: "we sold 2 cars that month" is
  // information, and it explains a gap in the line.
  const tableSeries = useMemo(() => {
    if (!split) {
      return combinedTrend.length
        ? [{ key: 'Alle', color: SERIES_COLORS[0], trend: combinedTrend }]
        : []
    }
    return merker.map((m, i) => ({
      key: groupBy === 'dealer-merke'
        ? pairKey(m.merke ?? '—', m.pairMerke ?? '—')
        : ((groupBy === 'modell' ? m.modell : m.merke) ?? '—'),
      color: groupBy === 'dealer-merke'
        ? (pairStyle.get(pairKey(m.merke ?? '—', m.pairMerke ?? '—'))?.color ?? SERIES_COLORS[0])
        : SERIES_COLORS[i % SERIES_COLORS.length],
      trend: m.trend,
    }))
     
  }, [split, merker, combinedTrend, groupBy, pairStyle])

  // Month chips come from whichever trend source is active.
  const months = useMemo(() => {
    const src = split ? merker.flatMap(m => m.trend) : combinedTrend
    return [...new Set(src.map(t => t.maned))].sort()
  }, [split, merker, combinedTrend])

  // Long-tail ståtid outliers (some roundtrips sit 500+ days) were
  // stretching the Y-axis so far that the median trend line — usually
  // 30-90 days — collapsed to a near-invisible flat line at the bottom
  // (reported: "the trendline... locks like a piece of shit"). Cap the
  // axis a bit above the 95th percentile of what's actually plotted; a few
  // extreme points render off the top edge, same trade-off the "utvalg"
  // sampling note below already makes for the point count.
  const yDomainMax = useMemo(() => {
    if (rows.length === 0) return 100
    const sorted = [...rows].map(r => r.staatid_dager).sort((a, b) => a - b)
    const p95 = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]
    return Math.max(Math.ceil(p95 * 1.15), 30)
  }, [rows])

  const clippedCount = useMemo(
    () => rows.filter(r => r.staatid_dager > yDomainMax).length,
    [rows, yDomainMax],
  )

  // Legend entries carry the series' own numbers, not just its name (#490
  // AC3/AC6) — reading "TOYOTA" told you nothing about whether that line is
  // built on 8 sales or 800.
  const seriesStats = useMemo(() => {
    const map = new Map<string, { antall: number; median: number | null; snitt: number | null }>()
    if (!split) {
      if (combinedStats) {
        map.set('Alle', {
          antall: combinedStats.antall,
          median: combinedStats.median_dager,
          snitt: combinedStats.snitt_dager,
        })
      }
      return map
    }
    for (const m of merker) {
      const key = groupBy === 'dealer-merke'
        ? pairKey(m.merke ?? '—', m.pairMerke ?? '—')
        : ((groupBy === 'modell' ? m.modell : m.merke) ?? '—')
      map.set(key, { antall: m.antall, median: m.median_dager, snitt: m.snitt_dager })
    }
    return map
     
  }, [split, merker, groupBy, combinedStats])

  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="chart-skeleton" className="animate-pulse h-72 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  // Ordered to match `merker` (trendLines' source, e.g. volume-desc for
  // dealer mode) rather than alphabetically — a series' dots and its own
  // trend line must land on the SAME color index. Sorting this list
  // independently of merker's order let a dealer's dots and its line get
  // different colors from SERIES_COLORS whenever alphabetical order
  // didn't happen to match the backend's order.
  const orderedNames = groupBy === 'dealer-merke'
    ? merker.map(m => pairKey(m.merke ?? '—', m.pairMerke ?? '—'))
    : merker.map(m => (groupBy === 'modell' ? m.modell : m.merke) ?? '—')
  const extraKeys = Object.keys(byMerke).filter(k => !orderedNames.includes(k)).sort()
  const merkeKeys = [...orderedNames.filter(k => byMerke[k]), ...extraKeys]
  const splitLabel = groupBy === 'dealer-merke' ? t('scatter.split.dealerMerke')
    : groupBy === 'dealer' ? t('scatter.split.dealer') : groupBy === 'modell' ? t('scatter.split.modell') : t('scatter.split.merke')
  /** Legend text: the series name plus its own n / median / snitt (#490). The
   *  entry name is the display label, optionally suffixed " median"/" snitt",
   *  so the stats are looked up on the label with that suffix removed. */
  const legendLabel = (value: string) => {
    const base = value.replace(new RegExp(` (${t('scatter.suffixMedian')}|${t('scatter.suffixSnitt')})$`), '')
    const key = [...seriesStats.keys()].find(k => displayLabel(k) === base)
    const st = key !== undefined ? seriesStats.get(key) : undefined
    if (!st) return value
    return t('scatter.legendStats', {
      value, n: st.antall.toLocaleString('nb-NO'), median: fmtDager(st.median), snitt: fmtDager(st.snitt),
    })
  }

  // Un-split, the one series is keyed 'Alle'; only its label is translated (#714).
  const displayLabel = (key: string) => {
    const name = !split && key === 'Alle' ? t('common.alle') : key
    return groupBy === 'dealer-merke' ? (pairStyle.get(name)?.label ?? name)
      : groupBy === 'dealer' && seriesLabelSuffix ? `${name} — ${seriesLabelSuffix}`
      : name
  }

  return (
    <div className="card p-4" data-testid="staatid-merke-scatter">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('common.enkeltsalgTitle')}{split ? `, ${splitLabel}` : ''}
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="staatid-toggle-dots"
            aria-pressed={showDots}
            onClick={() => setShowDots(v => !v)}
            className={`text-xs rounded border px-2 py-0.5 transition-colors ${
              showDots
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800'
                : 'bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500 border-gray-200 dark:border-dark-border'
            }`}
          >
            {t('scatter.togglePunkter')}
          </button>
          <button
            type="button"
            data-testid="staatid-toggle-snitt"
            aria-pressed={showSnitt}
            onClick={() => setShowSnitt(v => !v)}
            className={`text-xs rounded border px-2 py-0.5 transition-colors ${
              showSnitt
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800'
                : 'bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500 border-gray-200 dark:border-dark-border'
            }`}
          >
            {t('scatter.toggleSnitt')}
          </button>
          <button
            type="button"
            data-testid="staatid-toggle-punkter"
            aria-pressed={showPunkter}
            onClick={() => setShowPunkter(v => !v)}
            title={t('scatter.toggleMaanedspunktTitle')}
            className={`text-xs rounded border px-2 py-0.5 transition-colors ${
              showPunkter
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800'
                : 'bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500 border-gray-200 dark:border-dark-border'
            }`}
          >
            {t('scatter.toggleMaanedspunkt')}
          </button>
          <button
            type="button"
            data-testid="staatid-toggle-lines"
            aria-pressed={showLines}
            onClick={() => setShowLines(v => !v)}
            className={`text-xs rounded border px-2 py-0.5 transition-colors ${
              showLines
                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800'
                : 'bg-gray-50 dark:bg-dark-card text-gray-400 dark:text-gray-500 border-gray-200 dark:border-dark-border'
            }`}
          >
            {t('scatter.toggleTrend')}
          </button>
          {zoomMonth && (
            <button
              data-testid="staatid-zoom-reset"
              onClick={() => onZoomMonth(null)}
              className="text-xs rounded-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 hover:underline"
            >
              {t('scatter.zoomReset', { month: zoomMonth })}
            </button>
          )}
        </div>
      </div>
      {/* #531 — which line is Snitt and which is Trend was impossible to tell:
          the recharts legend names the SERIES, and in split mode the lines get
          no entry at all because the dots already carry one. This says what
          each line style means, in words. */}
      <div
        data-testid="staatid-scatter-legend"
        className="flex flex-wrap gap-x-5 gap-y-1 mb-3 text-xs"
      >
        <LegendKey label={t('common.maaned')} hint={t('scatter.legendMaanedHint')} />
        <LegendKey
          label={t('common.antallSluttsalg')}
          hint={showTable ? t('scatter.legendAntallHintTable') : t('scatter.legendAntallHintHover')}
        />
        <LegendKey label={t('common.median')} hint={t('scatter.legendMedianHint')} stroke="solid" />
        {showPunkter && (
          <LegendKey label={t('scatter.legendMedianPerMaaned')} hint={t('scatter.legendMedianPerMaanedHint')} stroke="dotted" />
        )}
        {showSnitt && <LegendKey label={t('common.snitt')} hint={t('scatter.legendSnittHint')} stroke="dashed" />}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('common.noData')}</p>
      ) : (
        <HoverLayer
          geometry={geometry}
          dotHover={dotHover}
          series={tableSeries.map(s => ({ ...s, label: displayLabel(s.key) }))}
        >
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="ts"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(ts: number) => new Date(ts).toLocaleDateString(dateLocale(), { day: '2-digit', month: 'short' })}
                tick={{ fontSize: 11 }}
              />
              {/* allowDataOverflow is required — Recharts' default (false) silently
                  widens an explicit domain back out to fit every data point,
                  which is exactly the outlier-driven stretching this domain
                  exists to prevent (found via Playwright: the axis rendered up
                  to the real outlier max regardless of yDomainMax). */}
              <YAxis
                dataKey="dager" type="number" domain={[0, yDomainMax]} allowDataOverflow
                tick={{ fontSize: 11 }} unit=" d"
              />
              {/* Hands the plot area and x scale to HoverLayer. Renders nothing. */}
              <Customized
                component={(chart: {
                  offset?: { left: number; top: number; width: number; height: number }
                  xAxisMap?: Record<string, { scale: { invert?: (x: number) => number } }>
                }) => {
                  const invert = chart.xAxisMap?.[0]?.scale.invert
                  geometry.current = chart.offset && invert ? { ...chart.offset, invert } : null
                  return null
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={legendLabel} />
              {/* Scatter and Line keys must be distinct even when they share a
                  series name (e.g. both "TOYOTA") — they're separate arrays of
                  children flattened into the same ComposedChart parent, so a
                  shared key is a real collision. Found via Playwright: the
                  rendered Line path had exactly as many vertices as the raw
                  scatter row count, meaning React's reconciler had cross-wired
                  the two components — this is what actually made the trend
                  line look like garbage, not a lack of curve smoothing. */}
              {showDots && merkeKeys.map((k, i) => (
                <Scatter
                  key={`scatter-${k}`}
                  name={displayLabel(k)}
                  data={byMerke[k]}
                  fill={
                    !split ? '#06b6d4'
                      : groupBy === 'dealer-merke' ? (pairStyle.get(k)?.color ?? SERIES_COLORS[0])
                      : SERIES_COLORS[i % SERIES_COLORS.length]
                  }
                  fillOpacity={0.45}
                  shape="circle"
                  legendType={split ? 'circle' : 'none'}
                  cursor={onPointClick ? 'pointer' : undefined}
                  onMouseEnter={(point: unknown) => dotHover.current(point as Point)}
                  onMouseLeave={() => dotHover.current(null)}
                  onClick={
                    onPointClick
                      ? (point: unknown) => onPointClick((point as Point).sluttSvvId)
                      : undefined
                  }
                />
              ))}
              {showLines && trendLines.map((l, i) => (
                <Line
                  // Pair mode (#471) can emit multiple runs (segments) per
                  // pair when a thin month gaps the line — l.name repeats
                  // across runs of the same pair, so the index keeps these
                  // distinct (same collision class as the scatter/line fix
                  // above, just within the Line array this time).
                  key={`line-${l.name}-${i}`}
                  type="monotone"
                  data={l.pts}
                  dataKey="median"
                  name={`${displayLabel(l.name)} ${t('scatter.suffixMedian')}`}
                  stroke={l.color}
                  strokeDasharray={l.dash || undefined}
                  strokeWidth={2.5}
                  dot={false}
                  // Scatter already supplies per-series legend entries when both
                  // are visible — only give Line its own entry when dots are
                  // hidden, so "lines only" mode still identifies each series.
                  // In pair mode, only the first run of a given pair (l.legend)
                  // gets an entry, so a gapped line doesn't duplicate its label.
                  // Un-split mode has no per-series scatter entry at all, so the
                  // combined line always carries the legend there (#490 AC6).
                  legendType={l.legend && (!split || !showDots) ? 'line' : 'none'}
                  isAnimationActive={false}
                />
              ))}
              {/* #531 AC5 — the raw monthly medians joined straight, with a dot
                  per month. type="linear" on purpose: this line exists precisely
                  because the smoothed one does not pass through the points. */}
              {showLines && showPunkter && trendLines.map((l, i) => (
                l.rawPts.length >= 2 ? (
                  <Line
                    key={`punkt-${l.name}-${i}`}
                    type="linear"
                    data={l.rawPts}
                    dataKey="median"
                    name={`${displayLabel(l.name)} ${t('scatter.suffixMedianPerMaaned')}`}
                    stroke={l.color}
                    strokeWidth={1.25}
                    strokeDasharray="1 3"
                    dot={{ r: 3, fill: l.color }}
                    legendType="none"
                    isAnimationActive={false}
                  />
                ) : null
              ))}
              {showLines && showSnitt && trendLines.map((l, i) => (
                l.snittPts.length >= 2 ? (
                  <Line
                    key={`snitt-${l.name}-${i}`}
                    type="monotone"
                    data={l.snittPts}
                    dataKey="median"
                    name={`${displayLabel(l.name)} ${t('scatter.suffixSnitt')}`}
                    stroke={l.color}
                    strokeDasharray="4 4"
                    strokeWidth={1.75}
                    strokeOpacity={0.85}
                    dot={false}
                    legendType={l.legend ? 'line' : 'none'}
                    isAnimationActive={false}
                  />
                ) : null
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </HoverLayer>
      )}
      {months.length > 1 && !zoomMonth && (
        <div className="flex flex-wrap gap-1 mt-2 items-center">
          <span className="text-xs text-gray-500 dark:text-gray-400 mr-1">{t('scatter.zoomTo')}</span>
          {months.map(m => (
            <button
              key={m}
              data-testid={`staatid-zoom-${m}`}
              onClick={() => onZoomMonth(m)}
              className="text-xs rounded-sm bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 px-2 py-0.5 hover:bg-gray-200 dark:hover:bg-dark-border transition-colors"
            >
              {m}
            </button>
          ))}
        </div>
      )}
      {total > rows.length && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          {t('scatter.utvalgZoom', { shown: rows.length.toLocaleString('nb-NO'), total: total.toLocaleString('nb-NO') })}
        </p>
      )}
      {clippedCount > 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {clippedCount === 1
            ? t('scatter.clippedOne', { n: clippedCount.toLocaleString('nb-NO'), max: yDomainMax })
            : t('scatter.clippedMany', { n: clippedCount.toLocaleString('nb-NO'), max: yDomainMax })}
        </p>
      )}

      {/* #531 AC1/AC2 — the numbers behind the plot. Built from the same
          arrays the lines are, so the table cannot disagree with the chart. */}
      {showTable && tableSeries.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            {t('scatter.tableTitle')}
          </h4>
          <div className="overflow-auto max-h-96">
            <table className="w-full text-sm" data-testid="staatid-scatter-table">
              <thead className="sticky top-0">
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
                  {tableSeries.length > 1 && (
                    <th className="px-3 py-1.5 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.serie')}</th>
                  )}
                  <th className="px-3 py-1.5 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.maaned')}</th>
                  <th className="px-3 py-1.5 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antallSluttsalg')}</th>
                  <th className="px-3 py-1.5 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
                  <th className="px-3 py-1.5 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
                </tr>
              </thead>
              <tbody>
                {tableSeries.flatMap(s =>
                  [...s.trend].sort((a, b) => a.maned.localeCompare(b.maned)).map((t, i) => (
                    <tr
                      key={`${s.key}-${t.maned}`}
                      data-testid="staatid-scatter-table-row"
                      className={`border-b border-gray-100 dark:border-dark-border ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                    >
                      {tableSeries.length > 1 && (
                        <td className="px-3 py-1.5">
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle"
                            style={{ backgroundColor: s.color }}
                            aria-hidden
                          />
                          {displayLabel(s.key)}
                        </td>
                      )}
                      <td className="px-3 py-1.5">{t.maned}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {t.antall === undefined ? '—' : t.antall.toLocaleString('nb-NO')}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtDager(t.median_dager)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtDager(t.snitt_dager)}</td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
